import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { useFormik } from 'formik';
import toast from 'react-hot-toast';
import { useClient } from 'jsonapi-react';
import StepName from './StepName';
import StepVideolesson from './StepVideolesson';
import StepVideoQuestions from './StepVideoQuestions';
import { useTranslation } from 'react-i18next';
import promiseRequest from '@/utils/promiseToast';
import ScheduleAlert from '@/components/custom/schedule-alert';
import ScheduleModal from '@/components/custom/schedule-modal';
import moment from 'moment';
import { getLessonPlayerType } from '@/utils/lessonPlayerType';

export default function LessonContent(props) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { promiseToast, isSubmitting } = promiseRequest();
  const { setCurrentStep, step, steps, currentStep, learningSystemContentId, moduleId, disciplineId, editLesson, abilities, courseId, setIsSlide, isSlide } = props;

  const client = useClient();
  const [taxonomies, setTaxonomies] = useState([]);
  const [isOpenModalTaxonomies, setIsOpenModalTaxonomies] = useState(false);
  const [currentDisciplineId, setCurrentDisciplineId] = useState(learningSystemContentId);
  const [lessons, setLessons] = useState([]);
  const [deletedQuestions, setDeletedQuestions] = useState([]);
  const { lessonId } = useParams();
  const [loading, setLoading] = useState(true);
  const [originalIds, setOriginalIds] = useState();
  const [urlError, setUrlError] = useState();
  const [showScheduleAlert, setShowScheduleAlert] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const location = useLocation();
  const [navigateLink, setNavigateLink] = useState(null);
  const [loadingTaxonomies, setLoadingTaxonomies] = useState(true);

  const getLessons = async () => {
    setLoading(true);
    const { data, error } = await client.fetch(`/learning_system_items?filter[learning_system_content_id]=${learningSystemContentId}`);
    if (error) {
      toast.error('Falha ao carregar aulas');
    } else {
      setLessons(data);
      const currentLesson = data.find(item => item.id == lessonId);

      setOriginalIds(currentLesson?.quiz_items?.map(item => item.id));
      createLessonForm?.setFieldValue('unrequired', !currentLesson?.unrequired);
      createLessonForm?.setFieldValue('body', currentLesson?.body);
      createLessonForm?.setFieldValue(
        'selectedTaxonomies',
        currentLesson?.taxonomies.map(item => ({ label: item.name, value: item.id }))
      );
      createLessonForm?.setFieldValue('url', currentLesson?.url);
      createLessonForm?.setFieldValue('quizItemsAttributes', currentLesson?.quiz_items);
      createLessonForm?.setFieldValue('published_at', currentLesson?.['published_at']);
      createLessonForm?.setFieldValue('published', currentLesson?.['published']);
      createLessonForm?.setFieldValue('lessonType', currentLesson?.lesson_type === 'slides' ? 'slides' : 'video');
      createLessonForm?.setFieldValue(
        'slideDeck',
        currentLesson?.slide_deck
          ? {
              id: currentLesson.slide_deck.id,
              status: currentLesson.slide_deck.status,
              pageCount: currentLesson.slide_deck.page_count,
              processedPages: currentLesson.slide_deck.processed_pages,
              errorMessage: null
            }
          : null
      );
      setLoading(false);
    }
  };

  useEffect(() => {
    if (lessonId) {
      getLessons();
    } else {
      setLoading(false);
    }
  }, []);

  const createLessonForm = useFormik({
    initialValues: {
      body: '',
      'learning-system-content-id': learningSystemContentId,
      url: '',
      lessonType: 'video',
      slideDeck: null,
      selectedTaxonomies: null,
      quizItemsAttributes: [],
      learningSystemQuestionBooksAttributes: [],
      learningSystemFilesAttributes: [],
      learningSystemActivitiesAttributes: [],
      unrequired: true
    },
    onSubmit: () => {
      handleSubmit;
    }
  });

  const handleLessonTypeChange = nextType => {
    if (nextType === createLessonForm.values.lessonType) return;

    const anchoredQuestions = createLessonForm.values.quizItemsAttributes || [];
    if (anchoredQuestions.length > 0) {
      if (!window.confirm(t('lessons.switchTypeDiscardQuestions'))) return;

      // Perguntas já persistidas precisam de _destroy no PATCH; as não salvas somem do form.
      const persistedQuestions = anchoredQuestions.filter(item => item.id && (originalIds || []).includes(item.id));
      if (persistedQuestions.length > 0) {
        setDeletedQuestions([...deletedQuestions, ...persistedQuestions]);
      }
      createLessonForm.setFieldValue('quizItemsAttributes', []);
    }

    createLessonForm.setFieldValue('lessonType', nextType);
    if (nextType === 'slides') {
      createLessonForm.setFieldValue('url', '');
      setIsSlide(false);
    } else {
      createLessonForm.setFieldValue('slideDeck', null);
    }
    setUrlError(null);
  };

  const fetchTaxonomies = async () => {
    setLoadingTaxonomies(true);
    try {
      const { data } = await client.fetch('taxonomies');
      setTaxonomies(data);
    } catch (e) {
      toast.error(t('toast.errorGetTags'));
    } finally {
      setLoadingTaxonomies(false);
    }
  };

  useEffect(() => {
    fetchTaxonomies();
  }, []);

  const createLesson = async () => {
    const isSlides = createLessonForm.values.lessonType === 'slides';

    if (!createLessonForm.values.url && !createLessonForm.values.slideDeck?.id) {
      return toast.error(t('lessons.errorLessonWithoutContent'));
    }

    if (isSlides && createLessonForm.values.slideDeck?.status !== 'ready') {
      return toast.error(t('lessons.slideDeckNotReady'));
    }

    const auxValues = { ...createLessonForm.values };

    auxValues.unrequired = !auxValues.unrequired;

    auxValues['taxonomy-ids'] = auxValues.selectedTaxonomies.map(item => item.value || item.id);

    const mapQuizItemAttributes = item => {
      const attributes = {
        id: item.id,
        'quiz-item-alternatives-attributes': item.quiz_item_alternatives,
        body: item.body,
        'taxonomy-ids': (item.selectedTaxonomies || []).map(taxonomy => taxonomy.id)
      };

      if (isSlides) {
        attributes['slide-position'] = item.slide_position;
      } else {
        attributes.duration = item.duration;
      }

      return attributes;
    };

    let quizItemUpdate = [];

    if (lessonId) {
      quizItemUpdate = auxValues.quizItemsAttributes.map(mapQuizItemAttributes);
    }
    let quizItemDelete = deletedQuestions.map(item => {
      return { id: item.id, _destroy: 1 };
    });

    let quizItemCreate = [];
    if (!lessonId) {
      quizItemCreate = auxValues.quizItemsAttributes.map(mapQuizItemAttributes);
    }

    auxValues['quiz-items-attributes'] = [...quizItemDelete, ...quizItemUpdate, ...quizItemCreate];

    auxValues['learning-system-question-books-attributes'] = [];

    auxValues['learning-system-files-attributes'] = [];

    auxValues['learning-system-activities-attributes'] = [];

    auxValues['slide-deck-id'] = isSlides ? auxValues.slideDeck.id : null;
    if (isSlides) auxValues.url = null;

    delete auxValues.selectedTaxonomies;
    delete auxValues.learningSystemQuestionBooksAttributes;
    delete auxValues.learningSystemFilesAttributes;
    delete auxValues.learningSystemActivitiesAttributes;
    delete auxValues.quizItemsAttributes;
    delete auxValues.lessonType;
    delete auxValues.slideDeck;

    const path = lessonId ? ['/learning_system_items', lessonId] : '/learning_system_items';

    promiseToast({
      url: path,
      request: auxValues,
      successText: 'Salvo com sucesso!',
      errorText: 'Erro ao salvar Aula' //default
    })
      .then(data => {
        if (data) {
          setCurrentDisciplineId(data.id);
          setShowScheduleAlert(true);
        }
      })
      .catch(error => {
        const anchorErrorIds = ['url', 'base', 'slide_position', 'slide-deck-id', 'slide_deck_id'];
        if (anchorErrorIds.includes(error?.id)) toast.error(error?.title);
      });
  };

  const handleCloseAlertSchedule = () => {
    setShowScheduleAlert(false);
    navigate(`/curso/admin/gerenciar/${courseId}/${moduleId}/disciplinas-e-aulas/${disciplineId}`, {
      state: {
        courseId: courseId,
        moduleId: moduleId,
        disciplineId: disciplineId
      }
    });
  };

  const btnsStyles = {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '24px'
  };
  const [submited, setSubmited] = useState(false);

  useEffect(() => {
    if (submited) {
      if (!Boolean(createLessonForm.values.body)) {
        setBodyError('Campo obrigatório');
      }
      if (!Boolean(createLessonForm.values.selectedTaxonomies)) {
        setTaxonomieError('Campo obrigatório');
      }
    }
  }, [createLessonForm.values, submited]);

  const [bodyError, setBodyError] = useState();
  const [taxonomieError, setTaxonomieError] = useState();

  const handleSubmit = e => {
    setSubmited(true);
    e.preventDefault();
    switch (step) {
      case 'nome':
        if (lessonId) {
          if (Boolean(createLessonForm.values.body) && Boolean(createLessonForm.values.selectedTaxonomies)) {
            navigate(`/lms/criar/aula/${learningSystemContentId}/videoaula/${lessonId}/${courseId}`, { state: { moduleId: moduleId, disciplineId: disciplineId, courseId: location.state?.courseId, editLesson: true } });
            setCurrentStep(1);
            setSubmited(false);
          }
          break;
        } else {
          if (Boolean(createLessonForm.values.body) && Boolean(createLessonForm.values.selectedTaxonomies)) {
            navigate(`/lms/criar/aula/${learningSystemContentId}/videoaula/${courseId}`, {
              state: { moduleId: moduleId, disciplineId: disciplineId, courseId: location.state?.courseId }
            });
            setCurrentStep(1);
            setSubmited(false);
          }
          break;
        }
      case 'videoaula': {
        if (createLessonForm.values.lessonType === 'slides') {
          if (!createLessonForm.values.slideDeck?.id) {
            return setUrlError(t('warning.requiredField'));
          }
          if (createLessonForm.values.slideDeck.status !== 'ready') {
            return setUrlError(t('lessons.slideDeckNotReady'));
          }
          setUrlError(null);
          if (!abilities?.quiz) {
            createLesson();
            break;
          }
          // Diferente do Genially, o modo slides MANTÉM o step de perguntas
          if (lessonId) {
            navigate(`/lms/criar/aula/${learningSystemContentId}/perguntas/${lessonId}/${courseId}`, {
              state: {
                moduleId: location.state?.moduleId,
                disciplineId: location.state?.disciplineId,
                courseId: location.state?.courseId
              }
            });
          } else {
            navigate(`/lms/criar/aula/${learningSystemContentId}/perguntas/${courseId}`, {
              state: {
                moduleId: location.state?.moduleId,
                disciplineId: location.state?.disciplineId,
                courseId: location.state?.courseId
              }
            });
          }
          setSubmited(false);
          setCurrentStep(2);
          break;
        }

        if (createLessonForm.values.url) {
          const { player } = getLessonPlayerType(createLessonForm.values.url);
          if (player === 'youtube' || player === 'vimeo') {
            if (lessonId && abilities?.quiz) {
              navigate(`/lms/criar/aula/${learningSystemContentId}/perguntas/${lessonId}/${courseId}`, {
                state: {
                  moduleId: location.state?.moduleId,
                  disciplineId: location.state?.disciplineId,
                  courseId: location.state?.courseId
                }
              });
              setSubmited(false);
              setCurrentStep(2);
              setUrlError(null);
            } else {
              if (!abilities.quiz) {
                createLesson();
              } else {
                (navigate(`/lms/criar/aula/${learningSystemContentId}/perguntas/${courseId}`),
                  {
                    state: {
                      moduleId: location.state?.moduleId,
                      disciplineId: location.state?.disciplineId,
                      courseId: location.state?.courseId
                    }
                  });
                setSubmited(false);
                setCurrentStep(2);
              }
            }
          } else if (player === 'genially') {
            createLesson();
          } else {
            if (submited) {
              setUrlError('Link do vídeo inválido');
            }
          }
          break;
        } else if (!createLessonForm.values.url) {
          return setUrlError(t('warning.requiredField'));
        }
      }
      case 'perguntas':
        createLesson();
        break;
    }
  };

  const updateSchedule = async publishedDate => {
    const scheduleDate = publishedDate ? { 'published-at': moment(publishedDate).format() } : { published: true };

    const { error } = await client.mutate(`/learning_system_items/${lessonId ? lessonId : currentDisciplineId}`, scheduleDate);

    if (error) {
      toast.error('Erro ao agendar aula');
      return;
    }
  };

  useEffect(() => {
    setNavigateLink(`/curso/admin/gerenciar/${courseId}/${moduleId}/disciplinas-e-aulas/${disciplineId}`, {
      state: {
        courseId: courseId,
        moduleId: moduleId,
        disciplineId: disciplineId
      }
    });
  }, [location]);

  return (
    <div className="page page--wrap">
      <div className="page__col">
        <form
          className="form form--step-by-step"
          method="post"
          onSubmit={handleSubmit}
        >
          <StepName
            loadingTaxonomies={loadingTaxonomies}
            bodyError={bodyError}
            taxonomieError={taxonomieError}
            step={step}
            createLessonForm={createLessonForm}
            isOpenModalTaxonomies={isOpenModalTaxonomies}
            setIsOpenModalTaxonomies={setIsOpenModalTaxonomies}
            loading={loading}
            taxonomies={taxonomies}
            editLesson={editLesson}
          />

          <StepVideolesson
            urlError={urlError}
            step={step}
            createLessonForm={createLessonForm}
            editLesson={editLesson}
            setIsSlide={setIsSlide}
            onLessonTypeChange={handleLessonTypeChange}
          />

          <StepVideoQuestions
            setDeletedQuestions={setDeletedQuestions}
            deletedQuestions={deletedQuestions}
            step={step}
            createLessonForm={createLessonForm}
            lessonId={lessonId}
            lessons={lessons}
            editLesson={editLesson}
          />

          <div style={btnsStyles}>
            {step !== 'nome' && (
              <button
                type="button"
                className="btn btn--outline btn--wide"
                onClick={() => {
                  if (lessonId) {
                    navigate(`/lms/criar/aula/${learningSystemContentId}/${steps?.[currentStep - 1]?.route}/${lessonId}/${location.state?.courseId}`, {
                      state: {
                        moduleId: location.state?.moduleId,
                        disciplineId: location.state?.disciplineId,
                        courseId: location.state?.courseId
                      }
                    });
                    setCurrentStep(currentStep - 1);
                  } else {
                    navigate(`/lms/criar/aula/${learningSystemContentId}/${steps?.[currentStep - 1]?.route}/${location.state?.courseId}`, {
                      state: {
                        moduleId: location.state?.moduleId,
                        disciplineId: location.state?.disciplineId,
                        courseId: location.state?.courseId
                      }
                    });
                    setCurrentStep(currentStep - 1);
                  }
                }}
              >
                {t('exams.previous')}
              </button>
            )}

            <button
              type="submit"
              className={isSubmitting ? 'btn btn--primary btn--wide disabled' : 'btn btn--primary'}
            >
              {(step === 'videoaula' && !abilities?.quiz) || (isSlide && step === 'videoaula') || step === 'perguntas' ? t('button.finish') : t('button.next')}
            </button>

            <ScheduleAlert
              show={showScheduleAlert}
              onClose={() => handleCloseAlertSchedule()}
              setShowModal={setShowModal}
              onUpdate={updateSchedule}
              navigateLink={navigateLink}
              title="Você deseja publicar a aula?"
            />

            <ScheduleModal
              show={showModal}
              onClose={() => setShowModal(true)}
              onUpdate={updateSchedule}
              navigateLink={navigateLink}
              fieldValue={lessonId ? moment(createLessonForm?.values?.['published-at']).format('YYYY-MM-DD') : ''}
            />
          </div>
        </form>
      </div>

      <div className="page__aside" />
    </div>
  );
}
